/* ============================================================
   VERITAS THEME — Main JavaScript
   Vanilla JS, no dependencies
   ============================================================ */

(function () {
  'use strict';

  /* ── Utilities ── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, ms) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); }; };
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return window.Shopify?.currency?.active === 'EUR' ? `€${amount}` : `£${amount}`;
  };

  /* ─────────────────────────────────────────────────
     HEADER — scroll shadow + sticky
  ───────────────────────────────────────────────── */
  function initHeader() {
    const header = $('.site-header');
    if (!header) return;
    const toggle = () => header.classList.toggle('scrolled', window.scrollY > 10);
    toggle();
    on(window, 'scroll', toggle, { passive: true });
  }

  /* ─────────────────────────────────────────────────
     MOBILE MENU
  ───────────────────────────────────────────────── */
  function initMobileMenu() {
    const menu = $('.mobile-menu');
    const open = $('.hamburger');
    const close = $('.mobile-menu__close');
    if (!menu || !open) return;
    const toggle = (state) => {
      menu.classList.toggle('open', state);
      open.classList.toggle('open', state);
      document.body.classList.toggle('no-scroll', state);
    };
    on(open, 'click', () => toggle(true));
    on(close, 'click', () => toggle(false));
    on(menu, 'click', (e) => { if (e.target === menu) toggle(false); });
  }

  /* ─────────────────────────────────────────────────
     ANNOUNCEMENT BAR
     The section already renders every message TWICE inside a single track,
     which is exactly what the `translateX(-50%)` marquee needs to loop
     seamlessly. Do NOT clone the track here — appending a second block-level
     track stacks it below the first and renders a doubled bar.
     This also strips any stray duplicate left by a cached older script.
  ───────────────────────────────────────────────── */
  function initAnnouncementBar() {
    const tracks = $$('.announcement-bar__track');
    tracks.forEach((track, i) => {
      if (i > 0) track.remove();
    });
  }

  /* ─────────────────────────────────────────────────
     HERO SLIDER
  ───────────────────────────────────────────────── */
  function initHeroSlider() {
    $$('.hero-slider').forEach((slider) => {
      const track = $('.hero-slider__track', slider);
      const dots = $$('.hero-slider__dot', slider);
      const prevBtn = $('.hero-slider__arrow--prev', slider);
      const nextBtn = $('.hero-slider__arrow--next', slider);
      const slides = $$('.hero-slide', slider);
      if (!track || slides.length < 2) return;

      let current = 0;
      let timer;

      const goTo = (idx) => {
        current = (idx + slides.length) % slides.length;
        track.style.transform = `translateX(-${current * 100}%)`;
        dots.forEach((d, i) => d.classList.toggle('active', i === current));
      };

      const next = () => goTo(current + 1);
      const prev = () => goTo(current - 1);

      const startAuto = () => { timer = setInterval(next, 5500); };
      const stopAuto = () => clearInterval(timer);

      dots.forEach((d, i) => on(d, 'click', () => { goTo(i); stopAuto(); startAuto(); }));
      on(nextBtn, 'click', () => { next(); stopAuto(); startAuto(); });
      on(prevBtn, 'click', () => { prev(); stopAuto(); startAuto(); });

      /* Touch/swipe */
      let touchX = 0;
      on(slider, 'touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
      on(slider, 'touchend', (e) => {
        const diff = touchX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) { diff > 0 ? next() : prev(); stopAuto(); startAuto(); }
      });

      goTo(0);
      startAuto();
      on(slider, 'mouseenter', stopAuto);
      on(slider, 'mouseleave', startAuto);
    });
  }

  /* ─────────────────────────────────────────────────
     GENERIC SLIDER (products / testimonials / etc.)
  ───────────────────────────────────────────────── */
  function initProductSliders() {
    $$('[data-slider]').forEach((wrap) => {
      const track = $('[data-slider-track]', wrap);
      const prevBtn = $('[data-slider-prev]', wrap);
      const nextBtn = $('[data-slider-next]', wrap);
      if (!track) return;

      let pos = 0;

      const getStep = () => {
        const items = track.children;
        if (!items.length) return 0;
        const itemW = items[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(track).gap) || 20;
        return itemW + gap;
      };

      const getMax = () => {
        const step = getStep();
        const visible = track.parentElement.getBoundingClientRect().width;
        const total = track.scrollWidth;
        return Math.max(0, total - visible);
      };

      const slide = (dir) => {
        const step = getStep();
        const max = getMax();
        pos = Math.max(0, Math.min(max, pos + dir * step));
        track.style.transform = `translateX(-${pos}px)`;
        if (prevBtn) prevBtn.disabled = pos <= 0;
        if (nextBtn) nextBtn.disabled = pos >= max;
      };

      on(prevBtn, 'click', () => slide(-1));
      on(nextBtn, 'click', () => slide(1));

      /* Touch */
      let tx = 0;
      on(track, 'touchstart', (e) => { tx = e.touches[0].clientX; }, { passive: true });
      on(track, 'touchend', (e) => {
        const diff = tx - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 30) slide(diff > 0 ? 1 : -1);
      });

      if (prevBtn) prevBtn.disabled = true;
    });
  }

  /* ─────────────────────────────────────────────────
     PRODUCT GALLERY
  ───────────────────────────────────────────────── */
  function initProductGallery() {
    const gallery = $('.product-gallery');
    if (!gallery) return;
    const mainImg = $('.product-gallery__main img', gallery);
    const thumbs = $$('.product-gallery__thumb', gallery);

    thumbs.forEach((thumb) => {
      on(thumb, 'click', () => {
        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
        if (mainImg) {
          mainImg.src = thumb.dataset.imgSrc || $('img', thumb)?.src;
          mainImg.srcset = thumb.dataset.imgSrcset || '';
        }
      });
    });

    /* Zoom toggle */
    const mainWrap = $('.product-gallery__main', gallery);
    on(mainWrap, 'click', () => mainWrap.classList.toggle('zoomed'));
  }

  /* ─────────────────────────────────────────────────
     PRODUCT OPTIONS & VARIANT SELECTOR
  ───────────────────────────────────────────────── */
  function initVariants() {
    const form = $('form[action="/cart/add"]');
    if (!form) return;

    const variantIdInput = $('[name="id"]', form);
    const priceEl = $('.js-price');
    const comparePriceEl = $('.js-compare-price');
    const addBtn = $('.btn--cart', form);
    const priceBadgeEl = $('.js-price-badge');

    $$('.variant-btn', form).forEach((btn) => {
      on(btn, 'click', () => {
        const group = btn.closest('[data-option]');
        $$('.variant-btn', group).forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        updateVariant(form);
      });
    });

    $$('.color-swatch', form).forEach((swatch) => {
      on(swatch, 'click', () => {
        const group = swatch.closest('[data-option]');
        $$('.color-swatch', group).forEach((s) => s.classList.remove('active'));
        swatch.classList.add('active');
        updateVariant(form);
      });
    });

    function updateVariant(frm) {
      const selected = {};
      $$('[data-option]', frm).forEach((group) => {
        const activeBtn = $('.variant-btn.active, .color-swatch.active', group);
        if (activeBtn) selected[group.dataset.option] = activeBtn.dataset.value;
      });

      const variants = window.__variants__ || [];
      const match = variants.find((v) =>
        Object.keys(selected).every((k) => v.options && v.options[k] === selected[k])
      );

      if (match) {
        if (variantIdInput) variantIdInput.value = match.id;
        if (priceEl) priceEl.textContent = formatMoney(match.price);
        if (comparePriceEl) {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            comparePriceEl.textContent = formatMoney(match.compare_at_price);
            comparePriceEl.style.display = '';
            if (priceBadgeEl) {
              const pct = Math.round((1 - match.price / match.compare_at_price) * 100);
              priceBadgeEl.textContent = `-${pct}%`;
              priceBadgeEl.style.display = '';
            }
          } else {
            comparePriceEl.style.display = 'none';
            if (priceBadgeEl) priceBadgeEl.style.display = 'none';
          }
        }
        if (addBtn) {
          addBtn.disabled = !match.available;
          addBtn.textContent = match.available ? addBtn.dataset.addText || 'Add to Cart' : 'Sold Out';
        }
      }
    }
  }

  /* ─────────────────────────────────────────────────
     QUANTITY PICKER
  ───────────────────────────────────────────────── */
  function initQtyPicker() {
    $$('.qty-picker').forEach((picker) => {
      const input = $('input', picker);
      const dec = $('[data-qty-dec]', picker);
      const inc = $('[data-qty-inc]', picker);
      if (!input) return;
      on(dec, 'click', () => { const v = parseInt(input.value) - 1; if (v >= 1) input.value = v; });
      on(inc, 'click', () => { input.value = parseInt(input.value) + 1; });
    });
  }

  /* ─────────────────────────────────────────────────
     CART — Add to cart + Drawer
  ───────────────────────────────────────────────── */
  let cartState = { items: [], total_price: 0 };

  async function fetchCart() {
    const res = await fetch('/cart.js');
    cartState = await res.json();
    return cartState;
  }

  async function addToCart(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    if (!res.ok) throw new Error('Failed to add');
    await fetchCart();
    renderCartDrawer();
    openCartDrawer();
    updateCartCount();
  }

  function updateCartCount() {
    $$('.cart-count').forEach((el) => {
      el.textContent = cartState.item_count || 0;
      el.dataset.count = cartState.item_count || 0;
    });
  }

  function renderCartDrawer() {
    const container = $('.cart-drawer__items');
    if (!container) return;

    if (!cartState.items || cartState.items.length === 0) {
      container.innerHTML = `
        <div class="cart-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
          <div class="cart-empty__title">Your cart is empty</div>
          <div class="cart-empty__text">Add items to continue</div>
        </div>`;
      const subtotal = $('.cart-subtotal span:last-child');
      if (subtotal) subtotal.textContent = formatMoney(0);
      return;
    }

    container.innerHTML = cartState.items.map((item) => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__img" src="${item.image}" alt="${item.title}" loading="lazy">
        <div class="cart-item__info">
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price-row">
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            <span class="cart-item__qty">x${item.quantity}</span>
            <button class="cart-item__remove" data-key="${item.key}">Remove</button>
          </div>
        </div>
      </div>`).join('');

    const subtotal = $('.cart-subtotal span:last-child');
    if (subtotal) subtotal.textContent = formatMoney(cartState.total_price);

    $$('.cart-item__remove').forEach((btn) => {
      on(btn, 'click', async () => {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.key, quantity: 0 }),
        });
        await fetchCart();
        renderCartDrawer();
        updateCartCount();
        updateShippingBar();
      });
    });

    updateShippingBar();
  }

  function openCartDrawer() {
    const drawer = $('.cart-drawer');
    const overlay = $('.cart-drawer-overlay');
    if (!drawer) return;
    drawer.classList.add('open');
    overlay.classList.add('open');
    document.body.classList.add('no-scroll');
  }

  function closeCartDrawer() {
    const drawer = $('.cart-drawer');
    const overlay = $('.cart-drawer-overlay');
    if (!drawer) return;
    drawer.classList.remove('open');
    overlay.classList.remove('open');
    document.body.classList.remove('no-scroll');
  }

  function initCartDrawer() {
    on($('.cart-drawer__close'), 'click', closeCartDrawer);
    on($('.cart-drawer-overlay'), 'click', closeCartDrawer);
    on($('.cart-drawer [data-cart-checkout]'), 'click', (e) => {
      e.preventDefault();
      window.location.href = window.__checkoutUrl__ || '/checkout';
    });
    $$('[data-open-cart]').forEach((btn) => {
      on(btn, 'click', async () => {
        await fetchCart();
        renderCartDrawer();
        openCartDrawer();
      });
    });
  }

  function initAddToCart() {
    $$('form[action="/cart/add"]').forEach((form) => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const idInput = $('[name="id"]', form);
        const qtyInput = $('[name="quantity"]', form);
        const btn = $('.btn--cart', form);
        if (!idInput || !idInput.value) return;
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;
        try {
          await addToCart(parseInt(idInput.value), parseInt(qtyInput?.value || 1));
          btn.textContent = 'Added!';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
        } catch {
          btn.textContent = 'Error — Try Again';
          btn.disabled = false;
        }
      });
    });

    /* Quick-add buttons on product cards */
    $$('[data-quick-add]').forEach((btn) => {
      on(btn, 'click', async (e) => {
        e.preventDefault();
        const vid = btn.dataset.variantId;
        if (!vid) return;
        btn.textContent = '...';
        try {
          await addToCart(parseInt(vid));
          btn.textContent = 'Added!';
          setTimeout(() => { btn.textContent = btn.dataset.originalText || 'Quick Add'; }, 2000);
        } catch {
          btn.textContent = 'Error';
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────
     FREE SHIPPING PROGRESS BAR
  ───────────────────────────────────────────────── */
  function updateShippingBar() {
    const bar = $('.shipping-progress');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.threshold || 8000); /* cents */
    const total = cartState.total_price || 0;
    const remaining = Math.max(0, threshold - total);
    const pct = Math.min(100, (total / threshold) * 100);
    const fill = $('.progress-bar__fill', bar);
    const text = $('.shipping-progress__text', bar);
    if (fill) fill.style.width = `${pct}%`;
    if (text) {
      text.innerHTML = remaining > 0
        ? `Add <strong>${formatMoney(remaining)}</strong> for free shipping`
        : '<strong>You have free shipping!</strong>';
    }
  }

  function initShippingBar() {
    const bar = $('.shipping-progress');
    if (!bar) return;
    fetchCart().then(() => updateShippingBar());
  }

  /* ─────────────────────────────────────────────────
     PRODUCT TABS
  ───────────────────────────────────────────────── */
  function initTabs() {
    $$('.product-tabs, [data-tabs]').forEach((wrap) => {
      const btns = $$('.tabs__btn', wrap);
      const panels = $$('.tabs__panel', wrap);
      btns.forEach((btn, i) => {
        on(btn, 'click', () => {
          btns.forEach((b) => b.classList.remove('active'));
          panels.forEach((p) => p.classList.remove('active'));
          btn.classList.add('active');
          panels[i] && panels[i].classList.add('active');
        });
      });
    });
  }

  /* ─────────────────────────────────────────────────
     STICKY ADD TO CART
  ───────────────────────────────────────────────── */
  function initStickyATC() {
    const sticky = $('.sticky-atc');
    const mainForm = $('form[action="/cart/add"]');
    if (!sticky || !mainForm) return;
    const observer = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle('visible', !entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(mainForm);
    on($('.sticky-atc [data-sticky-add]'), 'click', () => $('form[action="/cart/add"]')?.dispatchEvent(new Event('submit', { bubbles: true })));

    const buyNow = (btn) => {
      const form = $('form[action="/cart/add"]');
      if (!form || !btn) return;
      const original = btn.dataset.originalText || btn.textContent;
      btn.textContent = '...';
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
        .then(() => { window.location.href = window.__checkoutUrl__ || '/checkout'; })
        .catch(() => { btn.textContent = original; });
    };
    on($('.sticky-atc [data-sticky-buy-now]'), 'click', () => buyNow($('.sticky-atc [data-sticky-buy-now]')));
    $$('[data-buy-now]').forEach((btn) => on(btn, 'click', () => buyNow(btn)));
  }

  /* ─────────────────────────────────────────────────
     SOCIAL PROOF POPUP
  ───────────────────────────────────────────────── */
  function initSocialProof() {
    const popup = $('.sp-popup');
    if (!popup) return;

    const isDE = window.__locale__ === 'de';
    const cities = isDE
      ? ['Berlin', 'München', 'Hamburg', 'Frankfurt', 'Köln', 'Stuttgart', 'Düsseldorf', 'Leipzig', 'Dresden', 'Nürnberg', 'Bremen', 'Hannover']
      : ['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Edinburgh', 'Bristol', 'Liverpool', 'Oxford', 'Cambridge'];
    const names = isDE
      ? ['Max', 'Anna', 'Leon', 'Laura', 'Lukas', 'Marie', 'Felix', 'Sophie', 'Paul', 'Emma', 'Jonas', 'Lena', 'Tim', 'Julia', 'Nico', 'Mia']
      : ['James', 'Sarah', 'Liam', 'Emma', 'Noah', 'Olivia', 'Oliver', 'Amelia', 'Harry', 'Sophia', 'Jack', 'Alice', 'Thomas', 'Charlotte'];
    const actions = isDE
      ? ['hat gerade gekauft', 'hat gerade in den Warenkorb gelegt', 'sieht sich das gerade an', 'hat gerade bestellt']
      : ['just purchased', 'just added to cart', 'is viewing right now', 'just ordered'];
    const times = isDE
      ? ['vor 2 Minuten', 'vor 5 Minuten', 'vor 8 Minuten', 'vor 12 Minuten', 'gerade eben', 'vor 3 Minuten']
      : ['2 minutes ago', '5 minutes ago', '8 minutes ago', '12 minutes ago', 'just now', '3 minutes ago'];

    const products = window.__spProducts__ || [{ title: 'this product', img: '' }];
    const textEl = $('.sp-popup__text', popup);
    const imgEl = $('.sp-popup__img', popup);
    const timeEl = $('.sp-popup__time', popup);

    let showing = false;
    const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const show = () => {
      if (showing) return;
      showing = true;
      const product = rand(products);
      const name = rand(names);
      const city = rand(cities);
      const action = rand(actions);
      const time = rand(times);
      if (textEl) textEl.innerHTML = isDE
        ? `<strong>${name}</strong> aus ${city} ${action} <strong>${product.title}</strong>`
        : `<strong>${name}</strong> from ${city} ${action} <strong>${product.title}</strong>`;
      if (imgEl && product.img) { imgEl.src = product.img; imgEl.style.display = ''; }
      if (timeEl) timeEl.textContent = time;
      popup.classList.add('visible');
      setTimeout(() => {
        popup.classList.remove('visible');
        showing = false;
      }, 4500);
    };

    on(popup, 'click', () => { popup.classList.remove('visible'); showing = false; });

    /* First popup: 4-6 min after page load; then every 4-6 min */
    const loop = () => {
      show();
      setTimeout(loop, 240000 + Math.random() * 120000);
    };
    setTimeout(loop, 240000 + Math.random() * 120000);
  }

  /* ─────────────────────────────────────────────────
     LIVE ACTIVITY (product page)
  ───────────────────────────────────────────────── */
  function initLiveActivity() {
    const bar = $('.activity-bar');
    if (!bar) return;
    const countEl = $('.activity-bar__count', bar);
    if (!countEl) return;
    const base = parseInt(countEl.dataset.base || 0) || Math.floor(Math.random() * 12) + 3;
    countEl.textContent = base;
    let current = base;
    setInterval(() => {
      const delta = Math.random() > 0.4 ? 1 : -1;
      current = Math.max(1, Math.min(base + 8, current + delta));
      countEl.textContent = current;
    }, 7000 + Math.random() * 5000);
  }

  /* ─────────────────────────────────────────────────
     STOCK SCARCITY
  ───────────────────────────────────────────────── */
  function initScarcity() {
    $$('.scarcity-bar').forEach((bar) => {
      const fill = $('.progress-bar__fill', bar);
      const soldCount = $('.js-sold-count', bar);
      const stockLeft = $('.js-stock-left', bar);
      // Both figures are rendered server-side from the product id so they stay
      // consistent between page loads. Only fall back to a random value if the
      // data attributes are missing entirely.
      const base = parseInt(bar.dataset.stock || '', 10) || Math.floor(Math.random() * 19) + 4;
      const sold = parseInt(bar.dataset.sold || '', 10) || Math.floor(Math.random() * 47) + 18;
      const total = base + sold;
      const pct = total ? (sold / total) * 100 : 0;
      if (fill) fill.style.width = `${pct}%`;
      if (stockLeft) stockLeft.textContent = base;
      if (soldCount) soldCount.textContent = sold;
    });
  }

  /* ─────────────────────────────────────────────────
     URGENCY TIMER
  ───────────────────────────────────────────────── */
  function initUrgencyTimer() {
    $$('[data-urgency-timer]').forEach((el) => {
      const minutes = parseInt(el.dataset.minutes || 15);
      let remaining = minutes * 60;
      const update = () => {
        const m = String(Math.floor(remaining / 60)).padStart(2, '0');
        const s = String(remaining % 60).padStart(2, '0');
        el.textContent = `${m}:${s}`;
        if (remaining > 0) { remaining--; setTimeout(update, 1000); }
        else { el.textContent = '00:00'; el.style.color = 'var(--color-error)'; }
      };
      update();
    });
  }

  /* ─────────────────────────────────────────────────
     SCROLL ANIMATIONS
  ───────────────────────────────────────────────── */
  function initScrollAnimations() {
    const items = $$('[data-animate]');
    if (!('IntersectionObserver' in window)) {
      // No observer support → reveal everything immediately so content is never hidden.
      items.forEach((el) => el.classList.add('animated'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add('animated'), i * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((el) => observer.observe(el));
    // Failsafe: never leave content invisible. Reveal anything still un-animated
    // shortly after load (covers observer misfires, above-the-fold races, etc.).
    const revealAll = () => items.forEach((el) => el.classList.add('animated'));
    window.addEventListener('load', () => setTimeout(revealAll, 1200));
    setTimeout(revealAll, 2500);
  }

  /* ─────────────────────────────────────────────────
     LAZY IMAGES
  ───────────────────────────────────────────────── */
  function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) return; /* native lazy load */
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const img = e.target;
          if (img.dataset.src) { img.src = img.dataset.src; }
          if (img.dataset.srcset) { img.srcset = img.dataset.srcset; }
          observer.unobserve(img);
        }
      });
    }, { rootMargin: '200px' });
    $$('img[data-src]').forEach((img) => observer.observe(img));
  }

  /* ─────────────────────────────────────────────────
     RECENTLY VIEWED — LocalStorage
  ───────────────────────────────────────────────── */
  function initRecentlyViewed() {
    const productId = document.body.dataset.productId;
    const productData = window.__productData__;
    if (productId && productData) {
      let viewed = JSON.parse(localStorage.getItem('vt_rv') || '[]');
      viewed = viewed.filter((p) => p.id !== productData.id);
      viewed.unshift(productData);
      viewed = viewed.slice(0, 8);
      localStorage.setItem('vt_rv', JSON.stringify(viewed));
    }

    const container = $('.js-recently-viewed');
    if (!container) return;
    const viewed = JSON.parse(localStorage.getItem('vt_rv') || '[]').slice(1, 5);
    if (!viewed.length) { container.closest('.recently-viewed')?.remove(); return; }

    container.innerHTML = viewed.map((p) => `
      <a href="${p.url}" class="product-card">
        <div class="product-card__media">
          <img class="product-card__img" src="${p.image}" alt="${p.title}" loading="lazy">
        </div>
        <div class="product-card__body">
          <div class="product-card__title">${p.title}</div>
          <div class="product-card__price">
            <span class="price">${formatMoney(p.price)}</span>
          </div>
        </div>
      </a>`).join('');
  }

  /* ─────────────────────────────────────────────────
     WISHLIST — LocalStorage
  ───────────────────────────────────────────────── */
  function initWishlist() {
    const KEY = 'vt_wishlist';
    const get = () => JSON.parse(localStorage.getItem(KEY) || '[]');
    const save = (list) => localStorage.setItem(KEY, JSON.stringify(list));

    $$('[data-wishlist-toggle]').forEach((btn) => {
      const id = btn.dataset.wishlistToggle;
      const list = get();
      if (list.includes(id)) btn.classList.add('active');

      on(btn, 'click', (e) => {
        e.preventDefault();
        let current = get();
        if (current.includes(id)) {
          current = current.filter((i) => i !== id);
          btn.classList.remove('active');
        } else {
          current.push(id);
          btn.classList.add('active');
        }
        save(current);
      });
    });
  }

  /* ─────────────────────────────────────────────────
     BACK TO TOP
  ───────────────────────────────────────────────── */
  function initBackToTop() {
    const btn = $('.back-to-top');
    if (!btn) return;
    on(window, 'scroll', () => btn.classList.toggle('visible', window.scrollY > 500), { passive: true });
    on(btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ─────────────────────────────────────────────────
     NEWSLETTER FORM
  ───────────────────────────────────────────────── */
  function initNewsletter() {
    $$('.newsletter-form').forEach((form) => {
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const input = $('input[type="email"]', form);
        const btn = $('button', form);
        if (!input?.value) return;
        btn.textContent = 'Subscribing...';
        btn.disabled = true;
        try {
          const res = await fetch('/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ 'contact[email]': input.value, form_type: 'customer', utf8: '✓' }),
          });
          if (res.ok) {
            input.value = '';
            btn.textContent = 'Subscribed!';
          } else throw new Error();
        } catch {
          btn.textContent = 'Try Again';
          btn.disabled = false;
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────
     SEARCH DRAWER
  ───────────────────────────────────────────────── */
  function initSearch() {
    const drawer = $('.search-drawer');
    const input = drawer && $('input', drawer);
    const openBtns = $$('[data-open-search]');
    const closeBtn = drawer && $('.search-drawer__close');
    const results = drawer && $('.search-drawer__results');

    if (!drawer) return;

    const open = () => { drawer.classList.add('open'); input?.focus(); document.body.classList.add('no-scroll'); };
    const close = () => { drawer.classList.remove('open'); document.body.classList.remove('no-scroll'); };

    openBtns.forEach((b) => on(b, 'click', open));
    on(closeBtn, 'click', close);

    const search = debounce(async (q) => {
      if (!q || q.length < 2) { if (results) results.innerHTML = ''; return; }
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
      const data = await res.json();
      const hits = data.resources?.results?.products || [];
      if (!results) return;
      results.innerHTML = hits.length
        ? hits.map((p) => `
            <a href="${p.url}" class="search-result">
              <img src="${p.image?.url || p.featured_image?.url || ''}" alt="${p.title}" class="search-result__img" loading="lazy">
              <div class="search-result__info">
                <div class="search-result__title">${p.title}</div>
                <div class="search-result__price">${p.price ? formatMoney(parseInt(p.price)) : ''}</div>
              </div>
            </a>`).join('')
        : '<div class="search-empty">No results found</div>';
    }, 320);

    on(input, 'input', (e) => search(e.target.value));
  }

  /* ─────────────────────────────────────────────────
     CURRENCY SWITCHER (if markets enabled)
  ───────────────────────────────────────────────── */
  function initCurrencySwitcher() {
    const sel = $('[data-currency-switcher]');
    if (!sel) return;
    on(sel, 'change', (e) => {
      const form = sel.closest('form') || document.createElement('form');
      form.method = 'post';
      form.action = '/localization';
      const currency_code = document.createElement('input');
      currency_code.name = 'currency_code';
      currency_code.value = e.target.value;
      const return_to = document.createElement('input');
      return_to.name = 'return_to';
      return_to.value = window.location.pathname;
      form.append(currency_code, return_to);
      if (!form.parentElement) document.body.appendChild(form);
      form.submit();
    });
  }

  /* ─────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────── */
  function init() {
    initHeader();
    initMobileMenu();
    initAnnouncementBar();
    initHeroSlider();
    initProductSliders();
    initProductGallery();
    initVariants();
    initQtyPicker();
    initCartDrawer();
    initAddToCart();
    initShippingBar();
    initTabs();
    initStickyATC();
    initSocialProof();
    initLiveActivity();
    initScarcity();
    initUrgencyTimer();
    initScrollAnimations();
    initLazyImages();
    initRecentlyViewed();
    initWishlist();
    initBackToTop();
    initNewsletter();
    initSearch();
    initCurrencySwitcher();

    /* Init cart count from server */
    fetch('/cart.js').then((r) => r.json()).then((cart) => {
      cartState = cart;
      updateCartCount();
    });
  }

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', init);
  } else {
    init();
  }
})();
